import { useState, useMemo, useCallback } from 'react';

export interface UnsavedChangesOptions {
    /**
     * Whether we're in edit mode (true) or create mode (false)
     */
    isEditMode: boolean;
    
    /**
     * Current form data
     */
    formData: Record<string, any>;
    
    /**
     * Original data (for edit mode comparison)
     */
    originalData?: Record<string, any> | null;
    
    /**
     * Schema attributes or field definitions for comparison
     */
    schemaAttributes?: any[];
    
    /**
     * Custom function to check if there are changes (overrides default logic)
     */
    customHasChanges?: () => boolean;
    
    /**
     * Custom function to check if there are unsaved changes in create mode
     */
    customHasUnsavedChanges?: () => boolean;
}

export interface UseUnsavedChangesResult {
    /**
     * Whether there are unsaved changes
     */
    hasUnsavedChanges: boolean;
    
    /**
     * Whether the unsaved changes dialog is open
     */
    unsavedChangesDialogOpen: boolean;
    
    /**
     * Open the unsaved changes dialog
     */
    setUnsavedChangesDialogOpen: (open: boolean) => void;
    
    /**
     * Handle close attempt - returns true if should close, false if should show dialog
     */
    handleCloseAttempt: () => boolean;
    
    /**
     * Confirm discarding changes and close
     */
    handleConfirmDiscard: (onClose: () => void) => void;
}

/**
 * Custom hook for managing unsaved changes detection and confirmation.
 * Provides consistent logic for detecting and handling unsaved changes across all dialogs.
 */
export function useUnsavedChanges(
    options: UnsavedChangesOptions
): UseUnsavedChangesResult {
    const {
        isEditMode,
        formData,
        originalData,
        schemaAttributes = [],
        customHasChanges,
        customHasUnsavedChanges
    } = options;

    const [unsavedChangesDialogOpen, setUnsavedChangesDialogOpen] = useState(false);

    // Check if there are changes in edit mode (entity-change detection for save/close only; not tied to config version/date stamps)
    const hasChanges = useMemo(() => {
        if (customHasChanges) {
            return customHasChanges();
        }

        if (!isEditMode || !originalData) {
            return false;
        }

        if (schemaAttributes.length > 0) {
            // Schema-driven: compare only these attributes (name or id; never attr.value for entity key)
            for (const attr of schemaAttributes) {
                const name = attr.name || attr.id;
                const newValue = formData[name];
                const oldValue = originalData[name];
                if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                    return true;
                }
            }
            return false;
        }

        // No schema: compare all keys that appear in formData or originalData (dynamic, no hardcoded list)
        const allKeys = new Set([
            ...Object.keys(formData),
            ...Object.keys(originalData)
        ]);
        for (const name of allKeys) {
            const newValue = formData[name];
            const oldValue = originalData[name];
            if (JSON.stringify(newValue) !== JSON.stringify(oldValue)) {
                return true;
            }
        }
        return false;
    }, [isEditMode, originalData, formData, schemaAttributes, customHasChanges]);

    // Check if there are unsaved changes (for create mode, check if any form data has been entered)
    const hasUnsavedChanges = useMemo(() => {
        if (customHasUnsavedChanges) {
            return customHasUnsavedChanges();
        }

        if (isEditMode) {
            return hasChanges;
        }

        // For create mode, check if any form data has been entered
        // Check if any field has a non-empty value (name or id; never attr.value for entity key)
        for (const attr of schemaAttributes) {
            const name = attr.name || attr.id;
            const value = formData[name];

            // Check if value is meaningful (not empty, null, undefined, or empty array)
            if (value !== undefined && value !== null && value !== '') {
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        return true;
                    }
                } else if (typeof value === 'boolean') {
                    if (value === true) return true;
                } else if (typeof value === 'string') {
                    const trimmed = value.trim();
                    if (trimmed !== '' && trimmed !== '{}' && trimmed !== '[]') {
                        return true;
                    }
                } else if (typeof value === 'object') {
                    return true;
                } else {
                    return true;
                }
            }
        }

        if (schemaAttributes.length === 0) {
            const keys = Object.keys(formData);
            for (const key of keys) {
                const value = formData[key];
                if (value !== undefined && value !== null && value !== '') {
                    if (Array.isArray(value)) {
                        if (value.length > 0) {
                            return true;
                        }
                    } else if (typeof value === 'boolean') {
                        if (value === true) return true;
                    } else if (typeof value === 'string') {
                        const trimmed = value.trim();
                        if (trimmed !== '' && trimmed !== '{}' && trimmed !== '[]') {
                            return true;
                        }
                    } else if (typeof value === 'object') {
                        return true;
                    } else {
                        return true;
                    }
                }
            }
        }

        return false; // No meaningful data entered
    }, [isEditMode, hasChanges, formData, schemaAttributes, customHasUnsavedChanges]);

    const handleCloseAttempt = useCallback((): boolean => {
        // If there are unsaved changes, show dialog and return false (don't close)
        if (hasUnsavedChanges) {
            setUnsavedChangesDialogOpen(true);
            return false;
        }
        // No unsaved changes, return true (okay to close)
        return true;
    }, [hasUnsavedChanges]);

    const handleConfirmDiscard = useCallback((onClose: () => void) => {
        setUnsavedChangesDialogOpen(false);
        onClose();
    }, []);

    return {
        hasUnsavedChanges,
        unsavedChangesDialogOpen,
        setUnsavedChangesDialogOpen,
        handleCloseAttempt,
        handleConfirmDiscard
    };
}

